// src/components/Feedback.js
import React from 'react';
import axios from 'axios';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

const API_BASE = process.env.REACT_APP_API_ENDPOINT; 

export default class Feedback extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: '',
      feedback: [],

      // create form
      title: '',
      note: '',
      type: 'general',

      // user
      user: null, // { userId, firstName, lastName, ... }

      // comments
      activeCommentsId: null,
      commentText: '',

      // editing feedback
      editingFeedbackId: null,
      editingFeedbackTitle: '',
      editingFeedbackText: '',

      // editing comment
      editingCommentId: null,
      editingCommentText: '',
      editingCommentFeedbackId: null,
    };
  }

  componentDidMount() {
    this.fetchUserDetail();
  }

  //
  // 1. FETCH USER FIRST
  //
  fetchUserDetail = async () => {
    try {
      const res = await axios.get(`${API_BASE}/user/user_detail`, {
        withCredentials: true,
      });

      const raw = res.data || {};
      const user = {
        ...raw,
        // normalise id (some backends return _id, some return userId)
        userId: raw.userId || raw._id || raw.id,
      };

      this.setState({ user }, () => {
        this.fetchFeedback();
      });
    } catch (err) {
      console.error('Error fetching user detail:', err);
      this.setState({
        error: err?.response?.data?.message || 'Failed to fetch user detail',
      });
    }
  };

  //
  // 2. FETCH FEEDBACK LIST
  //
  fetchFeedback = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const res = await axios.get(`${API_BASE}/feedback`, {
        withCredentials: true,
      });
      this.setState({ feedback: res.data || [], loading: false });
    } catch (err) {
      this.setState({
        error: err?.response?.data?.message || 'Failed to load feedback',
        loading: false,
      });
    }
  };

  //
  // 3. CREATE FEEDBACK
  //
  handleCreate = async (e) => {
    e.preventDefault();
    const { title, note, type, user } = this.state;
    if (!user) return;
    if (!note.trim()) return;

    try {
      const res = await axios.post(
        `${API_BASE}/feedback`,
        {
          title,
          note,
          type,
          firstName: user.firstName,
          lastName: user.lastName,
        },
        { withCredentials: true }
      );

      this.setState((prev) => ({
        feedback: [res.data, ...prev.feedback],
        title: '',
        note: '',
        type: 'general',
      }));
    } catch (err) {
      this.setState({
        error: err?.response?.data?.message || 'Failed to create feedback',
      });
    }
  };

  //
  // 4. VOTE / UNVOTE
  //
  hasCurrentUserVoted = (fb) => {
    const { user } = this.state;
    if (!user) return false;
    return fb.vote?.some((v) => String(v.userId) === String(user.userId));
  };

  handleVote = async (fb) => {
    const { user } = this.state;
    if (!user) return;

    const currentUserId = user.userId;
    const id = fb._id;

    // cannot vote own feedback
    if (String(fb.userId) === String(currentUserId)) {
      this.setState({ error: "You can't vote on your own feedback." });
      return;
    }

    const alreadyVoted = this.hasCurrentUserVoted(fb);

    try {
      if (!alreadyVoted) {
        // add vote
        const res = await axios.post(
          `${API_BASE}/feedback/${id}/vote`,
          {
            userId: currentUserId,
            firstName: user.firstName,
            lastName: user.lastName,
          },
          { withCredentials: true }
        );
        this.updateFeedbackInState(res.data);
      } else {
        // remove vote
        const res = await axios.delete(`${API_BASE}/feedback/${id}/vote`, {
          withCredentials: true,
        });
        this.updateFeedbackInState(res.data);
      }
    } catch (err) {
      this.setState({
        error: err?.response?.data?.message || 'Failed to update vote',
      });
    }
  };

  //
  // 5. UPDATE FEEDBACK
  //
  startEditFeedback = (fb) => {
    this.setState({
      editingFeedbackId: fb._id,
      editingFeedbackTitle: fb.title || '',
      editingFeedbackText: fb.note || '',
    });
  };

  cancelEditFeedback = () => {
    this.setState({
      editingFeedbackId: null,
      editingFeedbackTitle: '',
      editingFeedbackText: '',
    });
  };

  saveEditFeedback = async () => {
    const {
      editingFeedbackId,
      editingFeedbackTitle,
      editingFeedbackText,
    } = this.state;
    if (!editingFeedbackId) return;

    try {
      const res = await axios.put(
        `${API_BASE}/feedback/${editingFeedbackId}`,
        {
          title: editingFeedbackTitle,
          note: editingFeedbackText,
        },
        { withCredentials: true }
      );
      this.updateFeedbackInState(res.data);
      this.cancelEditFeedback();
    } catch (err) {
      this.setState({
        error: err?.response?.data?.message || 'Failed to update feedback',
      });
    }
  };

  //
  // 6. DELETE FEEDBACK
  //
  handleDeleteFeedback = (fb) => {
    confirmAlert({
      title: 'Delete feedback?',
      message: 'This action cannot be undone.',
      buttons: [
        {
          label: 'Yes, delete',
          onClick: async () => {
            try {
              await axios.delete(`${API_BASE}/feedback/${fb._id}`, {
                withCredentials: true,
              });
              this.setState((prev) => ({
                feedback: prev.feedback.filter((f) => f._id !== fb._id),
              }));
            } catch (err) {
              this.setState({
                error:
                  err?.response?.data?.message ||
                  'Failed to delete feedback',
              });
            }
          },
        },
        { label: 'Cancel' },
      ],
    });
  };

  //
  // 7. COMMENTS: ADD
  //
  handleAddComment = async (fbId) => {
    const { commentText, user } = this.state;
    if (!commentText.trim() || !user) return;

    try {
      const res = await axios.post(
        `${API_BASE}/feedback/${fbId}/comments`,
        {
          userId: user.userId,
          firstName: user.firstName,
          lastName: user.lastName,
          comment: commentText,
        },
        { withCredentials: true }
      );
      this.updateFeedbackInState(res.data);
      this.setState({ commentText: '' });
    } catch (err) {
      this.setState({
        error: err?.response?.data?.message || 'Failed to add comment',
      });
    }
  };

  //
  // 8. COMMENTS: EDIT
  //
  startEditComment = (fbId, comment) => {
    this.setState({
      editingCommentId: comment._id,
      editingCommentText: comment.comment,
      editingCommentFeedbackId: fbId,
    });
  };

  cancelEditComment = () => {
    this.setState({
      editingCommentId: null,
      editingCommentText: '',
      editingCommentFeedbackId: null,
    });
  };

  saveEditComment = async () => {
    const {
      editingCommentId,
      editingCommentText,
      editingCommentFeedbackId,
    } = this.state;
    if (!editingCommentId || !editingCommentFeedbackId) return;

    try {
      const res = await axios.put(
        `${API_BASE}/feedback/${editingCommentFeedbackId}/comments/${editingCommentId}`,
        { comment: editingCommentText },
        { withCredentials: true }
      );
      this.updateFeedbackInState(res.data);
      this.cancelEditComment();
    } catch (err) {
      this.setState({
        error: err?.response?.data?.message || 'Failed to update comment',
      });
    }
  };

  //
  // 9. COMMENTS: DELETE
  //
  handleDeleteComment = (fbId, commentId) => {
    confirmAlert({
      title: 'Delete comment?',
      message: 'This action cannot be undone.',
      buttons: [
        {
          label: 'Yes, delete',
          onClick: async () => {
            try {
              const res = await axios.delete(
                `${API_BASE}/feedback/${fbId}/comments/${commentId}`,
                { withCredentials: true }
              );
              this.updateFeedbackInState(res.data);
            } catch (err) {
              this.setState({
                error:
                  err?.response?.data?.message ||
                  'Failed to delete comment',
              });
            }
          },
        },
        { label: 'Cancel' },
      ],
    });
  };

  //
  // helper to update one feedback item
  //
  updateFeedbackInState = (updated) => {
    this.setState((prev) => ({
      feedback: prev.feedback.map((fb) =>
        fb._id === updated._id ? updated : fb
      ),
    }));
  };

  //
  // toggle comments view
  //
  toggleComments = (id) => {
    this.setState((prev) => ({
      activeCommentsId: prev.activeCommentsId === id ? null : id,
      commentText: '',
    }));
  };

  render() {
    const {
      loading,
      error,
      feedback,
      title,
      note,
      type,
      activeCommentsId,
      commentText,
      user,
      editingFeedbackId,
      editingFeedbackTitle,
      editingFeedbackText,
      editingCommentId,
      editingCommentText,
    } = this.state;

    const currentUserId = user ? user.userId : null;

    return (
      <div className="p-20 bg-sky-800 min-h-screen">
        <h2 className="text-3xl text-center py-10 text-white font-bold mb-4">
          Feedback &amp; Suggestions
        </h2>

        {/* error */}
        {error && (
          <div className="mb-4 rounded-md bg-red-100 text-red-800 px-4 py-2 text-md">
            {error}
          </div>
        )}

        {/* create new feedback */}
        <form
          onSubmit={this.handleCreate}
          className="mb-6 bg-white rounded-lg shadow p-4 flex flex-col gap-3"
        >
          <div>
            <label className="block text-md font-medium text-slate-700 mb-1">
              Title
            </label>
            <input
              value={title}
              onChange={(e) => this.setState({ title: e.target.value })}
              className="w-full rounded-md border border-slate-200 p-2 text-md focus:outline-none focus:ring-2 focus:ring-sky-500"
              placeholder="e.g. Quick template for MHCP"
            />
          </div>

          <div>
            <label className="block text-md font-medium text-slate-700 mb-1">
              Feedback note
            </label>
            <textarea
              value={note}
              onChange={(e) => this.setState({ note: e.target.value })}
              className="w-full rounded-md border border-slate-200 p-2 text-md focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none h-32"
              placeholder="e.g. Can we add quick template for mental health care plan..."
            />
          </div>

          <div className="flex items-center gap-3">
            <div className="flex flex-row items-center gap-2">
              <label className="block text-md font-medium text-slate-700">
                Type
              </label>
              <select
                value={type}
                onChange={(e) => this.setState({ type: e.target.value })}
                className="rounded-md border border-slate-200 px-2 py-1 text-md focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <option value="general">General</option>
                <option value="bug">Bug</option>
                <option value="feature">Feature request</option>
                <option value="clinical">Clinical</option>
              </select>
            </div>

            <button
              type="submit"
              className="w-32 text-center bg-sky-700 hover:bg-sky-800 text-white text-md font-medium p-2 rounded-md shadow transition"
            >
              Submit
            </button>
          </div>
        </form>

        {/* list */}
        {loading ? (
          <p className="text-slate-200 text-md">Loading feedback…</p>
        ) : feedback.length === 0 ? (
          <p className="text-slate-200 text-md">No feedback yet.</p>
        ) : (
          <div className="space-y-4">
            {feedback.map((fb) => {
              const isOwn =
                currentUserId &&
                fb.userId &&
                String(fb.userId) === String(currentUserId);
              const voted = this.hasCurrentUserVoted(fb);

              return (
                <div
                  key={fb._id}
                  className="bg-slate-50 rounded-lg shadow p-4 border border-slate-100"
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1">
                      {/* edit feedback */}
                      {editingFeedbackId === fb._id ? (
                        <div className="flex flex-col gap-2">
                          <input
                            value={editingFeedbackTitle}
                            onChange={(e) =>
                              this.setState({
                                editingFeedbackTitle: e.target.value,
                              })
                            }
                            className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                            placeholder="Title"
                          />
                          <textarea
                            value={editingFeedbackText}
                            onChange={(e) =>
                              this.setState({
                                editingFeedbackText: e.target.value,
                              })
                            }
                            className="w-full rounded-md border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                            rows={3}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={this.saveEditFeedback}
                              className="bg-sky-600 text-white px-3 py-1 rounded-md text-xs"
                            >
                              Save
                            </button>
                            <button
                              onClick={this.cancelEditFeedback}
                              className="bg-slate-200 text-slate-700 px-3 py-1 rounded-md text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-lg font-semibold text-slate-900 mb-1">
                            {fb.title || 'Untitled feedback'}
                          </p>
                          <p className="text-slate-900 mb-1">{fb.note}</p>
                          <p className="text-xs text-slate-400">
                            {fb.firstName || ''} {fb.lastName || ''}{' '}
                            {fb.date
                              ? '• ' +
                                new Date(fb.date).toLocaleDateString('en-AU')
                              : ''}
                          </p>
                          <span className="inline-block mt-2 text-xs px-2 py-1 rounded bg-slate-100 text-slate-600">
                            {fb.type || 'general'}
                          </span>
                          <span className="inline-block mt-2 ml-2 text-xs px-2 py-1 rounded bg-amber-100 text-amber-700">
                            {fb.status || 'open'}
                          </span>
                        </>
                      )}
                    </div>

                    <div className="flex gap-2">
                      {/* vote button */}
                      <button
                        disabled={!currentUserId || isOwn}
                        onClick={() => this.handleVote(fb)}
                        className={`flex flex-col items-center justify-center min-w-[64px] rounded-md px-3 py-2 text-xs font-semibold transition-colors duration-200
                          ${
                            !currentUserId || isOwn
                              ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                              : fb.vote && fb.vote.length > 0
                              ? 'bg-yellow-400 text-yellow-900 hover:bg-yellow-500'
                              : 'bg-slate-500 text-white hover:bg-slate-600'
                          }`}
                      >
                        <span className="text-sm font-bold">
                          {fb.vote ? fb.vote.length : 0}
                        </span>
                        <span>
                          {isOwn ? 'My post' : voted ? 'Voted' : 'Vote'}
                        </span>
                      </button>

                      {/* edit & delete */}
                      {isOwn && editingFeedbackId !== fb._id && (
                        <button
                          onClick={() => this.startEditFeedback(fb)}
                          className="bg-slate-200 text-slate-800 text-xs px-3 py-2 rounded-md hover:bg-slate-300"
                        >
                          Edit
                        </button>
                      )}

                      {isOwn && (
                        <button
                          onClick={() => this.handleDeleteFeedback(fb)}
                          className="bg-red-500 hover:bg-red-600 text-white text-xs font-semibold px-3 py-2 rounded-md"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  {/* comments */}
                  <div className="mt-3">
                    <button
                      onClick={() => this.toggleComments(fb._id)}
                      className="text-xs text-sky-600 hover:text-sky-700"
                    >
                      {activeCommentsId === fb._id
                        ? 'Hide comments'
                        : `Comments (${
                            fb.comments ? fb.comments.length : 0
                          })`}
                    </button>

                    {activeCommentsId === fb._id && (
                      <div className="mt-3 space-y-3 rounded-md bg-slate-50 p-3">
                        {/* existing comments */}
                        {fb.comments && fb.comments.length > 0 ? (
                          fb.comments.map((c) => {
                            const canModifyComment =
                              currentUserId &&
                              (String(c.userId) === String(currentUserId) ||
                                String(fb.userId) === String(currentUserId));
                            const isEditingThisComment =
                              editingCommentId === c._id;

                            return (
                              <div
                                key={c._id}
                                className="bg-white rounded-md p-2 text-md flex justify-between items-start gap-2"
                              >
                                <div className="flex-1">
                                  {isEditingThisComment ? (
                                    <>
                                      <input
                                        value={editingCommentText}
                                        onChange={(e) =>
                                          this.setState({
                                            editingCommentText: e.target.value,
                                          })
                                        }
                                        className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                                      />
                                      <div className="flex gap-2 mt-2">
                                        <button
                                          onClick={this.saveEditComment}
                                          className="bg-sky-600 text-white px-3 py-1 rounded-md text-xs"
                                        >
                                          Save
                                        </button>
                                        <button
                                          onClick={this.cancelEditComment}
                                          className="bg-slate-200 text-slate-700 px-3 py-1 rounded-md text-xs"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <p className="text-slate-800">
                                        {c.comment}
                                      </p>
                                      <p className="text-[10px] text-slate-400 mt-1">
                                        {c.firstName} {c.lastName} •{' '}
                                        {c.date
                                          ? new Date(
                                              c.date
                                            ).toLocaleString('en-AU')
                                          : ''}
                                      </p>
                                    </>
                                  )}
                                </div>

                                {canModifyComment && !isEditingThisComment && (
                                  <div className="flex flex-col gap-1">
                                    <button
                                      onClick={() =>
                                        this.startEditComment(fb._id, c)
                                      }
                                      className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded hover:bg-slate-200"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() =>
                                        this.handleDeleteComment(fb._id, c._id)
                                      }
                                      className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                )}

                                {canModifyComment && isEditingThisComment && (
                                  <button
                                    onClick={this.cancelEditComment}
                                    className="text-xs text-slate-400"
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-xs text-slate-400">
                            No comments yet.
                          </p>
                        )}

                        {/* add comment */}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={commentText}
                            onChange={(e) =>
                              this.setState({ commentText: e.target.value })
                            }
                            className="flex-1 rounded-md border border-slate-200 px-2 py-1 text-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                            placeholder="Write a comment…"
                          />
                          <button
                            onClick={() => this.handleAddComment(fb._id)}
                            className="bg-sky-600 text-white text-md px-3 py-1 rounded-md hover:bg-sky-700"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
}
